import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { useSnackbar } from 'notistack';
import api from '../../services/api';
import { ADDONS_PAYMENT_SUCCESS, STRIPE_CREATE_SUBSCRIPTION } from '../../config/Config';

const DevPaymentForm = ({ planCode, planName, planPrice, changeReason, referralCode, cartItems, onSuccess, onCancel, userEmail }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    cardNumber: '4242424242424242',
    expiryMonth: '12',
    expiryYear: '2025',
    cvc: '123',
    name: 'Test User'
  });

  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    setLoading(true);
    
    try {
      // Simulate payment processing delay
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      let response;
      
      // Check if this is an add-on purchase or subscription
      if (planCode === 'addon_purchase') {
        // Handle add-on purchase
        const formattedCartItems = cartItems.map(item => ({
          addon_id: item.addon_id || 1, // Default to 1 if not provided
          quantity: item.quantity,
          total_price: item.total_price
        }));
        
        response = await api.post(ADDONS_PAYMENT_SUCCESS, {
          session_id: 'dev_session_' + Date.now(),
          cart_items: JSON.stringify(formattedCartItems)
        });
        
        enqueueSnackbar('Development payment successful! Add-ons purchased.', { variant: 'success' });
      } else {
        // Handle subscription creation
        const subscriptionData = {
          plan_code: planCode,
          change_reason: changeReason || 'User initiated plan change'
        };
        
        // Add referral code if available
        if (referralCode) {
          subscriptionData.referral_code = referralCode;
        }
        
        // Add user email for registration flow
        if (userEmail) {
          subscriptionData.email = userEmail;
        }
        
        response = await api.post(STRIPE_CREATE_SUBSCRIPTION, subscriptionData);
        
        enqueueSnackbar('Development payment successful! Subscription created.', { variant: 'success' });
      }
      
      if (onSuccess) {
        onSuccess(response.data);
      }
    } catch (err) {
      console.error('Error processing payment:', err);
      enqueueSnackbar('Failed to process payment', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card sx={{ maxWidth: 400, mx: 'auto' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Development Payment Form
        </Typography>
        
        <Alert severity="info" sx={{ mb: 2 }}>
          This is a development mode payment form. No real payment will be processed.
        </Alert>

        <Typography variant="body2" color="text.secondary" gutterBottom>
          Plan: {planName} - ${planPrice}
        </Typography>

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
          <TextField
            fullWidth
            label="Card Number"
            value={formData.cardNumber}
            onChange={handleInputChange('cardNumber')}
            margin="normal"
            disabled
          />
          
          <Box display="flex" gap={2} sx={{ mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Month</InputLabel>
              <Select
                value={formData.expiryMonth}
                onChange={handleInputChange('expiryMonth')}
                label="Month"
                disabled
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                  <MenuItem key={month} value={month.toString().padStart(2, '0')}>
                    {month.toString().padStart(2, '0')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl fullWidth>
              <InputLabel>Year</InputLabel>
              <Select
                value={formData.expiryYear}
                onChange={handleInputChange('expiryYear')}
                label="Year"
                disabled
              >
                {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() + i).map(year => (
                  <MenuItem key={year} value={year.toString()}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <TextField
              label="CVC"
              value={formData.cvc}
              onChange={handleInputChange('cvc')}
              margin="normal"
              disabled
              sx={{ width: 100 }}
            />
          </Box>
          
          <TextField
            fullWidth
            label="Cardholder Name"
            value={formData.name}
            onChange={handleInputChange('name')}
            margin="normal"
            disabled
          />

          {/* Stripe branding and security message */}
          <Box sx={{ mt: 3, mb: 2, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
              Your credit card details are not stored on our site. It is handled by Stripe.
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              Powered by{' '}
              <Box component="span" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                Stripe
              </Box>
            </Typography>
          </Box>

          <Box display="flex" gap={2} sx={{ mt: 3 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              fullWidth
            >
              {loading ? <CircularProgress size={20} /> : `Pay $${planPrice} (Dev Mode)`}
            </Button>
            
            {onCancel && (
              <Button
                variant="outlined"
                onClick={onCancel}
                disabled={loading}
              >
                Cancel
              </Button>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default DevPaymentForm; 